import { useState } from 'react';
import { authClient } from '@/api/auth';
import { useAppConfig } from '@/hooks/useAppConfig';

export function LoginPage() {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [devLoading, setDevLoading] = useState(false);
  const config = useAppConfig();

  async function handleDevLogin() {
    setError('');
    setDevLoading(true);
    try {
      const res = await fetch('/api/dev-login', { method: 'POST', credentials: 'include' });
      if (!res.ok) throw new Error('dev login failed');
      window.location.href = '/dashboard';
    } catch {
      setError('Dev sign-in failed. Is the backend running with dev tools enabled?');
      setDevLoading(false);
    }
  }

  // Surface the ?error=sso query param (set by Better Auth's errorCallbackURL
  // when Microsoft sign-in fails — usually because the user isn't in the
  // tenant or denied the consent prompt).
  const params = new URLSearchParams(window.location.search);
  const initialError = params.get('error') === 'sso'
    ? 'Microsoft sign-in failed. Make sure you used your work account.'
    : '';
  if (initialError && !error) setError(initialError);

  async function handleMicrosoft() {
    setError('');
    setLoading(true);
    try {
      // Redirects to Microsoft, then back to the backend callback, then to `callbackURL`.
      await authClient.signIn.social({
        provider: 'microsoft',
        callbackURL: `${window.location.origin}/dashboard`,
        errorCallbackURL: `${window.location.origin}/login?error=sso`,
      });
    } catch {
      setError('Could not start Microsoft sign-in');
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f0f0f5',
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 12,
        padding: '40px 36px',
        width: 360,
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
          <img
            src="/Asset%2042.png"
            alt="Labyrinth Studios"
            style={{ height: 240, width: 'auto', objectFit: 'contain' }}
          />
        </div>

        <p style={{ fontSize: 12, color: '#888', marginBottom: 28, textAlign: 'center', textTransform: 'uppercase', letterSpacing: 1.5 }}>
          Inventory & Quoting System
        </p>

        {error && (
          <div style={{
            background: '#fff0f0',
            border: '1px solid #fcc',
            borderRadius: 6,
            padding: '10px 14px',
            marginBottom: 16,
            fontSize: 13,
            color: '#c33',
          }}>
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={handleMicrosoft}
          disabled={loading}
          style={{
            width: '100%',
            padding: '12px 12px',
            background: loading ? '#f5f5f5' : '#fff',
            color: '#333',
            border: '1px solid #ddd',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
          }}
        >
          <MicrosoftLogo />
          {loading ? 'Redirecting…' : 'Sign in with Microsoft'}
        </button>

        <p style={{ fontSize: 11, color: '#999', textAlign: 'center', marginTop: 20, marginBottom: 0 }}>
          Sign-in is restricted to authorised company accounts.
        </p>

        {/* Dev-only local login — bypasses Microsoft SSO for testing. Only
            rendered when the backend reports dev login is enabled (never on
            production). */}
        {config?.devLogin && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '20px 0 14px' }}>
              <div style={{ flex: 1, height: 1, background: '#eee' }} />
              <span style={{ fontSize: 10, color: '#bbb', textTransform: 'uppercase', letterSpacing: 1 }}>Dev only</span>
              <div style={{ flex: 1, height: 1, background: '#eee' }} />
            </div>
            <button
              type="button"
              onClick={handleDevLogin}
              disabled={devLoading}
              style={{
                width: '100%',
                padding: '10px 12px',
                background: devLoading ? '#f5f5f5' : '#1a1a2e',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                cursor: devLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {devLoading ? 'Signing in…' : 'Dev sign-in (local)'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function MicrosoftLogo() {
  return (
    <svg width="16" height="16" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="1" y="1" width="10" height="10" fill="#f25022" />
      <rect x="12" y="1" width="10" height="10" fill="#7fba00" />
      <rect x="1" y="12" width="10" height="10" fill="#00a4ef" />
      <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
    </svg>
  );
}
